import { useEffect, useRef, useState } from "react";
import { AppError } from "../../../../stores/models/errors/AppError";
import { ITermsOfServiceFormValues } from "../../../authentication/_interfaces/ITermsOfServiceFormValues";
import { OffsetSelectionStepValues, VolunteerRegistrationFlowExternalState } from "../_interfaces/IAdminVolunteerRegistrationStepTypeValues";
import { IAdminVolunteerRegistrationFlowValues } from "../_interfaces/IAdminVolunteerRegistrationFlowValues";
import { TableController } from "../../../../stores/models/TableController";
import { OptionCollection } from "../../../../stores/models/OptionCollection";
import { Shift } from "../../../../stores/models/Shift";
import { AdminVolunteerRegistrationOffsetValidation } from "../validation/AdminVolunteerRegistrationOffsetValidation";
import { useRootStore } from "../../../../shared/general/hooks";
import { toJS } from "mobx";
import { AppErrors } from "../../../../stores/models/constants/AppErrors";

export function useOffsetSelectionController(
    values: OffsetSelectionStepValues,
    allValues: IAdminVolunteerRegistrationFlowValues,
    externallyManagedState: VolunteerRegistrationFlowExternalState,
    update: (patch: Partial<OffsetSelectionStepValues>) => void,
    onSuccess: (override?: Partial<OffsetSelectionStepValues>) => void
): {
    values: OffsetSelectionStepValues,
    onChange: (patch: Partial<OffsetSelectionStepValues>) => void,
    onSubmit: (override?: Partial<OffsetSelectionStepValues>) => void,
    isSubmitting: boolean,
    error: string | undefined,
    fieldErrors: Partial<Record<keyof OffsetSelectionStepValues, string>>
} {

    const rootStore = useRootStore();

    const [error, setError] = useState<string>();
    const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof ITermsOfServiceFormValues, string>>>({});
    const [isSubmitting, setSubmitting] = useState(false);
    const [validationRun, setValidationRun] = useState(false);

    // Set initial flow values:

    useEffect(() => {
        let patch: Partial<OffsetSelectionStepValues> = {
            backClicked: false,
            loading: false,
            succeeded: false,
        };
        if (!allValues.offsetsTableController) {
            patch.offsetsTableController = new TableController({
                records: new OptionCollection('id', []),
                loadRecords: async (request, lastRecordLoaded) => {
                    const topLevelShift = new Shift(externallyManagedState.topLevelShift);
                    const offset = allValues.offsets.length > 0 ? allValues.offsets[0] : undefined;
                    const currentShiftIdentification = offset === undefined ? topLevelShift.identificationData : { parentId: topLevelShift.id, defaultDaysFromStartDate: offset };
                    const currentShift = topLevelShift.getInstanceByIdentificationData(currentShiftIdentification);
                    const nextXInstances = topLevelShift.getNextXInstances(
                        request.limit,
                        lastRecordLoaded ? new Shift(lastRecordLoaded).repetitionPattern.startTimestamp! : currentShift!.repetitionPattern.startTimestamp!,
                        lastRecordLoaded ? false : true
                    ).map(shift => { return toJS(shift) });
                    return {
                        total: topLevelShift.getNumInstancesAfterTimestamp(currentShift?.repetitionPattern.startTimestamp!),
                        results: nextXInstances
                    }
                }
            })
        }
        update(patch);
    }, []);

    // Effects:

    // Close dialog upon successful registration
    useEffect(() => {
        if (values.succeeded) {
            externallyManagedState.dialogState.setOpen(false);
        }
    }, [values.succeeded]);

    useEffect(() => {
        externallyManagedState.dialogState.setLoading(values.loading);
    }, [values.loading]);

    useEffect(() => {
        if (values.offsetsTableController?.tableState.initialLoadCompleted) {
            values.offsetsTableController?.records.options.forEach(option => {
                if (values.offsets.findIndex(offset => offset === option.object.instanceData?.defaultDaysFromStartDate) !== -1 && !option.selected) {
                    option.toggleSelection();
                }
            })
        }
    }, [values.offsetsTableController?.tableState.initialLoadCompleted])

    useEffect(() => {
        if (values.offsetsTableController?.tableState.initialLoadCompleted) {
            update({ offsets: values.offsetsTableController?.records?.selectedOptions.map(shiftInstance => shiftInstance.instanceData?.defaultDaysFromStartDate).filter(offset => offset !== undefined) || [] })
        }
    }, [values.offsetsTableController?.records.selectedOptions.length])

    useEffect(() => {
        if (validationRun && !isSubmitting) {
            const errors = validate().errors;
            setFieldErrors(errors);
        }
    }, [values.offsets?.length, validationRun])

    // Validation:

    const validate = (override?: Partial<OffsetSelectionStepValues>) => {
        return AdminVolunteerRegistrationOffsetValidation({ ...externallyManagedState, ...allValues, ...override });
    }

    const isValidSubmission = (override?: Partial<OffsetSelectionStepValues>) => {
        return validate(override).valid;
    }

    // Submission:

    const submit = async (override?: Partial<OffsetSelectionStepValues>) => {
        setSubmitting(true);
        setError(undefined);
        setValidationRun(true);

        const effectiveValues = {
            ...allValues,
            ...override
        };

        try {
            const shouldSwitchStepsWithoutValidation = effectiveValues.backClicked;
            if (shouldSwitchStepsWithoutValidation) { // If the flow should move to a different step
                update({ ...override });
                onSuccess(override);
            } else if (isValidSubmission(override)) {
                externallyManagedState.dialogState.setLoading(true);
                update({ loading: true });
                const result = await rootStore.shiftStore.registerVolunteers(
                    effectiveValues.volunteerIds,
                    externallyManagedState.topLevelShift.id,
                    effectiveValues.offsets
                );
                update({ ...override, loading: false, succeeded: true });
                onSuccess({ ...override, loading: false, succeeded: true });
                if (allValues.onVolunteersRegistered && result) {
                    allValues.onVolunteersRegistered(result);
                }
            }
        } catch (err) {
            if (err instanceof AppError) {
                switch (err.code) {
                    case AppErrors.LIMIT_EXCEEDED.code:
                        setError("There are not enough remaining slots for one or more of the selected shifts.")
                        break;
                    default:
                        setError(err.message);
                }
            } else {
                setError("An unknown error occurred.");
            }
        } finally {
            externallyManagedState.dialogState.setLoading(false);
            update({ loading: false });
            setSubmitting(false);
        }
    };

    // Set up onClick handler:

    const submitRef = useRef(submit);

    useEffect(() => {
        submitRef.current = submit;
    }, [submit]);

    useEffect(() => {
        const registerHandler = () => {
            submitRef.current();
        };

        const backHandler = () => {
            submitRef.current({ backClicked: true });
        };

        externallyManagedState.dialogState.setPrimaryButton({
            children: "Register",
            onClick: registerHandler
        })

        externallyManagedState.dialogState.setLeftButton({
            children: "Back",
            onClick: backHandler
        })

        externallyManagedState.dialogState.setHideCancelButton(false);
    }, []);

    return {
        values,
        onChange: update,
        onSubmit: submit,
        isSubmitting,
        error,
        fieldErrors
    };
}